import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEvents, getAccessibleChats, publishEventToChat } from '../api/eventsApi';
import { updateMyTemplateDefaultChat } from '../api/templatesApi';
import { getOrgSettings } from '../api/tournamentsApi';
import { Spinner, PageError, pad2 } from '../components/UI';
import { useToast } from '../components/Toast';
import useRefreshOnVisible from '../hooks/useRefreshOnVisible';

// ─── Утилиты дат ──────────────────────────────────────────────────────────────

const MONTHS_SHORT = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];

function formatLocalDT(ldt) {
  // ldt — "2024-05-20T17:30:00" (LocalDateTime без зоны)
  if (!ldt) return null;
  const [datePart, timePart] = ldt.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, min] = (timePart || '00:00').split(':').map(Number);
  return { day: d, month: m, year: y, hour: h, minute: min };
}

function formatDateLabel(ldt) {
  const p = formatLocalDT(ldt);
  if (!p) return null;
  return `${pad2(p.day)} ${MONTHS_SHORT[p.month - 1]} ${p.year}`;
}

function formatTimeLabel(ldt) {
  const p = formatLocalDT(ldt);
  if (!p) return null;
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

function formatInstantLocal(isoInstant) {
  // createdAt — Instant в ISO формате, показываем в локальной зоне браузера
  if (!isoInstant) return null;
  const d = new Date(isoInstant);
  return `${pad2(d.getDate())} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}  ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// ─── Иконки ───────────────────────────────────────────────────────────────────

const IconCalendar = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconClock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const IconUsers = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const IconList = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

// Кубок в размер плюсика из CreateActionCard (10×10) — для чипа «Событие + Турнир».
const IconTrophySmall = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="8 21 12 17 16 21"/>
    <line x1="12" y1="17" x2="12" y2="11"/>
    <path d="M7 4V3H17V4"/>
    <path d="M7 4c0 5 5 8 5 8s5-3 5-8"/>
    <path d="M5 4H3c0 5 2 7 4 8"/>
    <path d="M19 4h2c0 5-2 7-4 8"/>
  </svg>
);

// Значок «i» — кнопка-подсказка рядом с карточками создания (CreateActionCard).
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="16" x2="12" y2="11.5"/>
    <line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>
);


// ─── EventCard ─────────────────────────────────────────────────────────────────

const EventCard = React.memo(({ event, isAnchor, onClick, onPublish, publishLoading }) => {
  const hasDate = !!event.eventDate;
  const hasEndDate = !!event.eventEndDate;
  const hasLimit = event.totalSlots != null;
  const isFull = hasLimit && event.confirmedCount >= event.totalSlots;
  const hasWaiting = event.waitingCount > 0;

  const dateLabel = hasDate ? formatDateLabel(event.eventDate) : null;
  const timeStart = hasDate ? formatTimeLabel(event.eventDate) : null;
  const timeEnd = hasEndDate ? formatTimeLabel(event.eventEndDate) : null;
  const createdLabel = !hasDate ? formatInstantLocal(event.createdAt) : null;

  // Статус-цвет
  const statusColor = event.status === 'CANCELLED'
    ? { bg: '#fee2e2', border: '#fca5a5', text: '#b91c1c', label: 'Отменено' }
    : event.status === 'COMPLETED'
    ? { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280', label: 'Завершено' }
    : null; // ACTIVE — без бейджа

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        borderRadius: 14,
        border: isAnchor
          ? '2px solid #6366f1'
          : '1.5px solid #f0f0f5',
        boxShadow: isAnchor
          ? '0 4px 16px rgba(99,102,241,0.13)'
          : '0 1px 6px rgba(0,0,0,0.05)',
        padding: '12px 14px',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.1s',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.985)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {/* Anchor stripe */}
      {isAnchor && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
          background: 'linear-gradient(180deg, #6366f1, #4f46e5)',
          borderRadius: '14px 0 0 14px',
        }} />
      )}

      {/* Top row: date + status badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hasDate ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6366f1', fontSize: 12, fontWeight: 700 }}>
                <IconCalendar />
                {dateLabel}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#6b7280', fontSize: 12 }}>
                <IconClock />
                {timeEnd ? `${timeStart} – ${timeEnd}` : timeStart}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#9ca3af', fontSize: 12 }}>
              <IconCalendar />
              <span>Создано {createdLabel}</span>
            </div>
          )}
        </div>

        {statusColor && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px',
            borderRadius: 99, border: `1px solid ${statusColor.border}`,
            background: statusColor.bg, color: statusColor.text,
            flexShrink: 0, marginLeft: 8,
          }}>
            {statusColor.label}
          </span>
        )}
      </div>

      {/* Title */}
      {event.title && (
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4, lineHeight: 1.3 }}>
          {event.title}
        </div>
      )}

      {/* Description preview */}
      {event.descriptionPreview && (
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.45, marginBottom: 8 }}>
          {event.descriptionPreview}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
        {/* Confirmed / slots */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: isFull ? '#fef3c7' : '#ede9fe',
          borderRadius: 99, padding: '3px 9px',
          border: `1px solid ${isFull ? '#fde68a' : '#c7d2fe'}`,
        }}>
          <span style={{ color: isFull ? '#d97706' : '#6366f1' }}><IconUsers /></span>
          <span style={{ fontSize: 12, fontWeight: 700, color: isFull ? '#92400e' : '#4f46e5' }}>
            {hasLimit
              ? `${event.confirmedCount} / ${event.totalSlots}`
              : event.confirmedCount}
          </span>
        </div>

        {/* Waiting */}
        {hasWaiting && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5,
            background: '#fef3c7', borderRadius: 99, padding: '3px 9px',
            border: '1px solid #fde68a',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
              Ожидают {event.waitingCount}
            </span>
          </div>
        )}

        {/* Waiting list enabled indicator (when no actual waiters yet) */}
        {!hasWaiting && event.waitingListEnabled === true && hasLimit && (
          <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
            📋 Лист ожидания включен
          </span>
        )}
        {!hasWaiting && event.waitingListEnabled === false && hasLimit && (
          <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>
            🔒 Без листа ожидания
          </span>
        )}
      </div>

      {/* Кнопка публикации — только когда per-chat данные получены */}
      {event.publishedToSelectedChat !== null && event.publishedToSelectedChat !== undefined && (
        <button
          onClick={e => { e.stopPropagation(); onPublish(event); }}
          disabled={publishLoading}
          style={{
            marginTop: 10,
            width: '100%',
            padding: '7px 12px',
            borderRadius: 9,
            border: event.publishedToSelectedChat
              ? '1.5px solid #c7d2fe'
              : '1.5px solid #a5b4fc',
            background: publishLoading
              ? '#f3f4f6'
              : event.publishedToSelectedChat
              ? '#f5f3ff'
              : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            color: publishLoading
              ? '#9ca3af'
              : event.publishedToSelectedChat
              ? '#4f46e5'
              : '#fff',
            fontSize: 12,
            fontWeight: 700,
            cursor: publishLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'all 0.15s',
          }}
        >
          {publishLoading ? (
            'Публикация...'
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              {event.publishedToSelectedChat ? 'Повторно опубликовать' : 'Опубликовать'}
            </>
          )}
        </button>
      )}
    </div>
  );
});
EventCard.displayName = 'EventCard';

// ─── Разделитель «Сейчас» ─────────────────────────────────────────────────────

const NowDivider = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
    <div style={{ flex: 1, height: 1.5, background: 'linear-gradient(90deg, transparent, #6366f1)' }} />
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      padding: '3px 12px', borderRadius: 99,
      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>Сейчас</span>
    </div>
    <div style={{ flex: 1, height: 1.5, background: 'linear-gradient(90deg, #6366f1, transparent)' }} />
  </div>
);

// ─── Карточки-заглушки «Создать …» ─────────────────────────────────────────────
// Внешняя пунктирная карточка — лёгкая, не перетягивает внимание, просто маркирует
// место в потоке списка. Сам текст внутри дополнительно обведён как кнопка-чип
// (сплошная рамка + белая заливка) — чтобы однозначно читаться как кликабельное
// действие, а не просто подпись.
// theme='indigo' — «Создать событие» (цвет самого списка событий).
// theme='amber'  — «Событие + Турнир» (цвет турнирного модуля, как в ViewEventPage).

const CARD_THEMES = {
  indigo: { dash: '#a5b4fc', border: '#6366f1', text: '#4f46e5', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)' },
  amber:  { dash: '#fde68a', border: '#d97706', text: '#b45309', gradient: 'linear-gradient(135deg, #d97706, #b45309)' },
};

const CreateActionCard = ({ label, onClick, theme = 'indigo', icon, onInfoClick }) => {
  const c = CARD_THEMES[theme];
  return (
    <div style={{
      background: '#fafaff',
      borderRadius: 14,
      border: `1.5px dashed ${c.dash}`,
      padding: '14px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      transition: 'background 0.15s',
    }}>
      {/* Сам чип-кнопка создания — клик и press-анимация теперь только на нём,
          не на всей пунктирной карточке, т.к. рядом появилась отдельная кнопка-подсказка. */}
      <div
        onClick={onClick}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: '#fff',
          border: `1.5px solid ${c.border}`,
          borderRadius: 99,
          padding: '6px 14px',
          cursor: 'pointer',
          transition: 'transform 0.1s',
        }}
        onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.985)'; }}
        onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%',
          background: c.gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon || (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          )}
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{label}</span>
      </div>

      {/* Кнопка-подсказка — рядом с чипом, но за его рамкой, крупнее для удобного тапа. */}
      {onInfoClick && (
        <button
          type="button"
          onClick={onInfoClick}
          title="Подробнее"
          aria-label="Подробнее"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            border: `1.5px solid ${c.border}`, background: '#fff', padding: 0,
            color: c.text, opacity: 0.85, cursor: 'pointer',
          }}
        >
          <IconInfo />
        </button>
      )}
    </div>
  );
};

// Текст всплывающих подсказок — открываются по клику на ⓘ рядом с карточками создания.
const INFO_CONTENT = {
  event: {
    title: 'Что такое событие?',
    text: 'Событие, на которое участники записываются сами, прямо в сообщении бота. Вы контролируете количество мест и лист ожидания: место освободилось, и следующий из очереди подтягивается автоматически. Ведите учёт посещаемости и оплаты, а коллегам можно назначить роль и делегировать им ввод этих отметок. Турнир к событию можно добавить позже, в любой момент.',
  },
  tournament: {
    title: 'Что такое «Событие + Турнир»?',
    text: 'То же самое, что «Создать событие»: самозапись, лимит мест, лист ожидания, учёт посещаемости и оплаты. Только сразу следующим шагом идёт настройка турнира, сначала форма события, потом формат и параметры турнира. Коллегам с назначенными ролями можно доверить параллельный ввод результатов матчей.',
  },
};

// Разделитель «Сейчас» + карточки создания — единый блок-якорь.
// К этому блоку список автоскроллится при открытии (см. anchorDivRef ниже),
// поэтому кнопки создания оказываются в центре экрана независимо от размера списка.
const NowSection = React.forwardRef(({ onCreateEvent, onCreateEventWithTournament, onInfo }, ref) => (
  <div ref={ref}>
    <NowDivider />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '8px 0' }}>
      <CreateActionCard
        label="Создать событие"
        onClick={onCreateEvent}
        theme="indigo"
        onInfoClick={() => onInfo?.('event')}
      />
      <CreateActionCard
        label="Событие + Турнир"
        onClick={onCreateEventWithTournament}
        theme="amber"
        icon={<IconTrophySmall />}
        onInfoClick={() => onInfo?.('tournament')}
      />
    </div>
  </div>
));
NowSection.displayName = 'NowSection';

// ─── Индикатор загрузки края ──────────────────────────────────────────────────

const EdgeLoader = ({ label }) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 0', color: '#9ca3af' }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
    <span style={{ fontSize: 13 }}>{label}</span>
  </div>
);

// ─── Главная страница ──────────────────────────────────────────────────────────

const EventsListPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [events, setEvents] = useState([]);
  const [anchorIndex, setAnchorIndex] = useState(0); // индекс разделителя «Сейчас»

  const [beforeCursor, setBeforeCursor] = useState(null);
  const [afterCursor, setAfterCursor] = useState(null);
  const [hasBefore, setHasBefore] = useState(false);
  const [hasAfter, setHasAfter] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingBefore, setLoadingBefore] = useState(false);
  const [loadingAfter, setLoadingAfter] = useState(false);
  const [pageError, setPageError] = useState(null);

  // ── Чат-селектор ────────────────────────────────────────────────────────
  // null = личный чат (chatId=0 на бэкенд), число = группа
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [accessibleChats, setAccessibleChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);

  // ── Публикация ───────────────────────────────────────────────────────────
  const [publishingEventId, setPublishingEventId] = useState(null); // publicId события в процессе публикации
  const [confirmRepublish, setConfirmRepublish] = useState(null);   // event-объект или null

  // ── Подсказки у карточек создания ──────────────────────────────────────────
  const [infoModal, setInfoModal] = useState(null); // 'event' | 'tournament' | null

  const scrollRef = useRef(null);
  // После препенда нужно скорректировать scrollTop
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  // Флаг: первый рендер после initial load — нужно спозиционироваться
  const shouldScrollToAnchorRef = useRef(false);
  // Флаг: скролл к anchor выполнен — только после этого разрешаем topObserver
  const anchorScrollDoneRef = useRef(false);
  const anchorDivRef = useRef(null);

  // ── Intersection Observer для автоподгрузки ──
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);

  // ── Загрузка доступных чатов и defaultChatId из шаблона ────────────────

  useEffect(() => {
    setChatsLoading(true);
    getAccessibleChats()
      .then(response => {
        setAccessibleChats(response.chats || []);
        if (response.defaultChatId != null) {
          setSelectedChatId(response.defaultChatId);
        }
      })
      .catch(err => console.warn('Failed to load chats:', err))
      .finally(() => setChatsLoading(false));
  }, []);

  // ── Начальная загрузка (перезапускается при смене чата) ───────────────────

  const loadInitial = useCallback(async () => {
    setInitialLoading(true);
    setPageError(null);
    // Сбрасываем пагинацию при смене чата
    setEvents([]);
    setBeforeCursor(null);
    setAfterCursor(null);
    setHasBefore(false);
    setHasAfter(false);
    anchorScrollDoneRef.current = false;
    try {
      // null → chatId=0 (личный чат, бэкенд резолвит в userId)
      const chatIdParam = selectedChatId ?? 0;
      const data = await getEvents({ direction: 'initial', chatId: chatIdParam });
      setEvents(data.events || []);
      setAnchorIndex(data.anchorIndex ?? 0);
      setBeforeCursor(data.beforeCursor);
      setAfterCursor(data.afterCursor);
      setHasBefore(data.hasBefore);
      setHasAfter(data.hasAfter);
      shouldScrollToAnchorRef.current = true;
    } catch (err) {
      setPageError(err.message || 'Не удалось загрузить события');
    } finally {
      setInitialLoading(false);
    }
  }, [selectedChatId]);

  // chatsLoading используется как gate: loadInitial не запускается пока не загрузились
  // чаты и шаблон (иначе первый запрос уйдёт с chatId=0, а не с defaultChatId из шаблона)
  useEffect(() => {
    if (chatsLoading) return;
    loadInitial();
  }, [loadInitial, chatsLoading]);

  // При возврате на свёрнутую страницу после долгого отсутствия — перезагружаем
  // список и пере-центрируем на «сейчас» (данные могли устареть).
  useRefreshOnVisible(loadInitial, { enabled: !chatsLoading });

  // ── Скролл к разделителю после первой загрузки ────────────────────────────

  useEffect(() => {
    if (!initialLoading && shouldScrollToAnchorRef.current && anchorDivRef.current) {
      shouldScrollToAnchorRef.current = false;
      // requestAnimationFrame гарантирует что браузер уже выполнил layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          anchorDivRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
          // Разрешаем topObserver только после того как скролл выставлен
          anchorScrollDoneRef.current = true;
        });
      });
    }
  }, [initialLoading, events]);

  // ── Подгрузка старых событий (вверх) ─────────────────────────────────────

  const loadBefore = useCallback(async () => {
    if (loadingBefore || !hasBefore || !beforeCursor) return;
    setLoadingBefore(true);

    try {
      const data = await getEvents({ direction: 'before', cursor: beforeCursor, chatId: selectedChatId ?? 0 });
      if (data.events?.length) {
        // Запоминаем scrollTop и scrollHeight ПРЯМО ПЕРЕД мутацией DOM
        const el = scrollRef.current;
        const scrollHeightBefore = el?.scrollHeight ?? 0;
        const scrollTopBefore = el?.scrollTop ?? 0;

        setEvents(prev => [...data.events, ...prev]);
        setAnchorIndex(prev => prev + data.events.length);
        setBeforeCursor(data.beforeCursor);
        setHasBefore(data.hasBefore);

        // Компенсируем прыжок сразу после setState в следующем frame
        prevScrollHeightRef.current = scrollHeightBefore;
        prevScrollTopRef.current = scrollTopBefore;
      }
    } catch (err) {
      console.error('loadBefore error:', err);
    } finally {
      setLoadingBefore(false);
    }
  }, [loadingBefore, hasBefore, beforeCursor, selectedChatId]);

  // Компенсация прыжка после prepend
  useEffect(() => {
    const el = scrollRef.current;
    if (!loadingBefore && prevScrollHeightRef.current && el) {
      requestAnimationFrame(() => {
        const diff = el.scrollHeight - prevScrollHeightRef.current;
        if (diff > 0) {
          el.scrollTop = prevScrollTopRef.current + diff;
        }
        prevScrollHeightRef.current = 0;
        prevScrollTopRef.current = 0;
      });
    }
  }, [loadingBefore, events]);

  // ── Подгрузка новых событий (вниз) ───────────────────────────────────────

  const loadAfter = useCallback(async () => {
    if (loadingAfter || !hasAfter || !afterCursor) return;
    setLoadingAfter(true);
    try {
      const data = await getEvents({ direction: 'after', cursor: afterCursor, chatId: selectedChatId ?? 0 });
      if (data.events?.length) {
        setEvents(prev => [...prev, ...data.events]);
        setAfterCursor(data.afterCursor);
        setHasAfter(data.hasAfter);
      }
    } catch (err) {
      console.error('loadAfter error:', err);
    } finally {
      setLoadingAfter(false);
    }
  }, [loadingAfter, hasAfter, afterCursor, selectedChatId]);

  // ── IntersectionObserver ──────────────────────────────────────────────────

  useEffect(() => {
    const options = { root: scrollRef.current, rootMargin: '120px', threshold: 0 };

    const topObs = new IntersectionObserver(([entry]) => {
      // Не реагируем пока не выставили позицию после initial load
      if (entry.isIntersecting && anchorScrollDoneRef.current) loadBefore();
    }, options);

    const bottomObs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) loadAfter();
    }, options);

    if (topSentinelRef.current) topObs.observe(topSentinelRef.current);
    if (bottomSentinelRef.current) bottomObs.observe(bottomSentinelRef.current);

    return () => { topObs.disconnect(); bottomObs.disconnect(); };
  }, [loadBefore, loadAfter]);

  // ─────────────────────────────────────────────────────────────────────────

  // ── «Событие + Турнир» ──────────────────────────────────────────────────
  // Турнир не может существовать без события (event_id nullable=false на бэке),
  // поэтому сначала всегда создаём событие — CreateEventPage сам после сохранения
  // отправит на CreateTournamentPage (см. forTournament=1). Турниры — фича на уровне
  // организации, гейтим здесь же, до захода в форму, чтобы не тратить время
  // пользователя на заполнение формы ради ошибки в конце.
  const [checkingTournamentsGate, setCheckingTournamentsGate] = useState(false);

  const handleCreateEventWithTournament = async () => {
    if (checkingTournamentsGate) return;
    setCheckingTournamentsGate(true);
    try {
      const settings = await getOrgSettings();
      if (!settings?.tournamentsEnabled) {
        addToast('Сначала включите турниры в настройках организации', 'info');
        navigate('/settings');
        return;
      }
      navigate('/events/create?forTournament=1');
    } catch (err) {
      addToast(`Не удалось проверить настройки турниров: ${err.message}`, 'error');
    } finally {
      setCheckingTournamentsGate(false);
    }
  };

  // ── Публикация ────────────────────────────────────────────────────────────

  const doPublish = async (event) => {
    setPublishingEventId(event.publicId);
    try {
      await publishEventToChat(event.publicId, selectedChatId ?? null);
      // Обновляем флаг публикации прямо в списке без перезагрузки
      setEvents(prev => prev.map(e =>
        e.publicId === event.publicId
          ? { ...e, publishedToSelectedChat: true }
          : e
      ));
    } catch (err) {
      console.error('Publish error:', err);
    } finally {
      setPublishingEventId(null);
    }
  };

  const handlePublishClick = (event) => {
    if (event.publishedToSelectedChat) {
      setConfirmRepublish(event);
    } else {
      doPublish(event);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────

  if (initialLoading) return <Spinner text="Загрузка событий..." />;
  if (pageError) return <PageError message={pageError} onRetry={loadInitial} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        padding: '14px 16px 16px',
        color: '#fff',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconList />
            </div>
            <div>
              <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: -0.3 }}>
                Все события
              </h1>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Шестерёнка — настройки организации */}
            <button
              onClick={() => navigate('/settings')}
              title="Настройки организации"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 8, padding: '5px 8px',
                color: '#fff', cursor: 'pointer', backdropFilter: 'blur(4px)',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Sticky chat selector bar ── */}
      <div style={{
        flexShrink: 0,
        background: '#fff',
        borderBottom: '1px solid #f0f0f5',
        padding: '8px 16px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {/* Иконка чата */}
        <div style={{ flexShrink: 0, color: '#6366f1' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', flexShrink: 0 }}>
          Публикация в:
        </span>
        <div style={{ position: 'relative', flex: 1 }}>
          <select
            value={chatsLoading ? '' : (selectedChatId ?? '')}
            onChange={e => {
              const newChatId = e.target.value ? Number(e.target.value) : null;
              setSelectedChatId(newChatId);
              updateMyTemplateDefaultChat(newChatId)
                .catch(err => console.warn('Failed to save default chat:', err));
            }}
            disabled={chatsLoading}
            style={{
              width: '100%',
              padding: '6px 24px 6px 8px',
              borderRadius: 8,
              border: '1.5px solid #e0e7ff',
              fontSize: 13, fontWeight: 600,
              color: chatsLoading ? '#9ca3af' : '#111827',
              background: chatsLoading ? '#f3f4f6' : '#fafafe',
              outline: 'none',
              appearance: 'none', WebkitAppearance: 'none',
              cursor: chatsLoading ? 'wait' : 'pointer',
            }}
          >
            {chatsLoading ? (
              <option value="">Загрузка...</option>
            ) : (
              <>
                <option value="">Личный чат с ботом</option>
                {accessibleChats.map(chat => (
                  <option key={chat.telegramChatId} value={chat.telegramChatId}>
                    {chat.title || `Чат ${chat.telegramChatId}`}
                  </option>
                ))}
              </>
            )}
          </select>
          <div style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        </div>
      </div>

      {/* ── Scrollable list ── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {/* Top sentinel */}
        <div ref={topSentinelRef} style={{ height: 1 }} />

        {/* Loader вверху */}
        {loadingBefore && <EdgeLoader label="Загружаем более ранние..." />}

        {/* Конец прошлого */}
        {!hasBefore && events.length > 0 && (
          <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: 12, padding: '8px 0 12px', fontWeight: 500 }}>
            · начало списка ·
          </div>
        )}

        {/* Пустой список — тот же якорь «Сейчас» + карточка создания, что и в заполненном списке */}
        {events.length === 0 && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', gap: 12, padding: '24px 0',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#ede9fe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconList />
              </div>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#6b7280', margin: 0 }}>
                Событий пока нет
              </p>
            </div>
            <NowSection
              onCreateEvent={() => navigate('/events/create')}
              onCreateEventWithTournament={handleCreateEventWithTournament}
              onInfo={setInfoModal}
            />
          </div>
        )}

        {/* Список с разделителем */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.map((event, idx) => {
            const isAnchorItem = idx === anchorIndex;

            return (
              <React.Fragment key={event.publicId}>
                {/* Разделитель «Сейчас» + карточка создания — перед первым событием "после сейчас" */}
                {isAnchorItem && (
                  <NowSection
                    ref={anchorDivRef}
                    onCreateEvent={() => navigate('/events/create')}
                    onCreateEventWithTournament={handleCreateEventWithTournament}
                    onInfo={setInfoModal}
                  />
                )}
                <EventCard
                  event={event}
                  isAnchor={false}
                  onClick={() => navigate(`/events/${event.publicId}`)}
                  onPublish={handlePublishClick}
                  publishLoading={publishingEventId === event.publicId}
                />
              </React.Fragment>
            );
          })}

          {/* Если все события в прошлом — разделитель + карточка создания в конце */}
          {events.length > 0 && anchorIndex >= events.length && (
            <NowSection
              ref={anchorDivRef}
              onCreateEvent={() => navigate('/events/create')}
              onCreateEventWithTournament={handleCreateEventWithTournament}
              onInfo={setInfoModal}
            />
          )}
        </div>

        {/* Loader внизу */}
        {loadingAfter && <EdgeLoader label="Загружаем следующие..." />}

        {/* Конец будущего */}
        {!hasAfter && events.length > 0 && (
          <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: 12, padding: '12px 0 8px', fontWeight: 500 }}>
            · конец списка ·
          </div>
        )}

        {/* Bottom sentinel */}
        <div ref={bottomSentinelRef} style={{ height: 1 }} />
      </div>
      {/* ── Подсказка «Что такое событие / Событие + Турнир» — по клику на ⓘ у карточек создания ── */}
      {infoModal && (
        <div
          onClick={() => setInfoModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, padding: '22px 20px',
              width: '100%', maxWidth: 340,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
            }}
          >
            <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
              {INFO_CONTENT[infoModal].title}
            </h3>
            <p style={{ margin: '0 0 18px', fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
              {INFO_CONTENT[infoModal].text}
            </p>
            <button
              onClick={() => setInfoModal(null)}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Понятно
            </button>
          </div>
        </div>
      )}
      {/* ── Диалог подтверждения повторной публикации ── */}
      {confirmRepublish && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '24px 20px',
            width: '100%', maxWidth: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
              Повторная публикация
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
              Это событие уже было опубликовано в данный чат. Опубликовать повторно?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmRepublish(null)}
                style={{
                  flex: 1, padding: '11px 16px',
                  borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#f9fafb', color: '#374151',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  const ev = confirmRepublish;
                  setConfirmRepublish(null);
                  doPublish(ev);
                }}
                style={{
                  flex: 2, padding: '11px 16px',
                  borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Да, опубликовать
              </button>
            </div>
          </div>
          <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
        </div>
      )}
    </div>
  );
};

export default EventsListPage;